/**
 * Registry of long-running background processes the agent spawns.
 *
 * The metadata lives in the THREAD's durable key-value store (server-side), not
 * on this client — so the process list resumes from any machine, at any time,
 * and nothing about it is kept on the client. (The process *output* logs are the
 * one host-local thing: a detached process writes its stdout to a file on the
 * machine it runs on; that file can only live there.)
 *
 * Liveness is re-derived from the OS for this machine's processes — a recorded
 * "running" entry is verified with a signal-0 probe — so the registry self-heals
 * if a process died while we weren't watching. Processes on other machines show
 * their last-known status (we can't probe a PID we don't own).
 */
import path from "node:path";
import os from "node:os";
import type { ApiClient } from "./api.ts";

/** Process *output* logs are host-local (the process writes its stdout here). */
export const LOG_DIR = path.join(os.homedir(), ".standardagents", "process-logs");

/** The thread KV key under which the process list is stored. */
const KEY = "bg_processes";

export type ProcessStatus = "running" | "exited" | "stopped";

export interface ProcessEntry {
  id: string;
  pid: number;
  command: string;
  description?: string;
  cwd: string;
  machine: string;
  logPath: string;
  startedAt: number;
  status: ProcessStatus;
  exitCode?: number | null;
  endedAt?: number;
}

function isAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (err) {
    // EPERM means the process exists but isn't ours; ESRCH means it's gone.
    return (err as NodeJS.ErrnoException).code === "EPERM";
  }
}

export class ProcessRegistry {
  constructor(
    private api: ApiClient,
    private threadId: string,
    private machine: string
  ) {}

  private async read(): Promise<ProcessEntry[]> {
    const value = await this.api.kvGet(this.threadId, KEY);
    return Array.isArray(value) ? (value as ProcessEntry[]) : [];
  }

  private async write(entries: ProcessEntry[]): Promise<void> {
    await this.api.kvSet(this.threadId, KEY, entries);
  }

  /** Mark this machine's dead "running" entries as exited. Returns true if any changed. */
  private reconcileLiveness(entries: ProcessEntry[]): boolean {
    let changed = false;
    for (const e of entries) {
      if (e.machine === this.machine && e.status === "running" && !isAlive(e.pid)) {
        e.status = "exited";
        e.endedAt = Date.now();
        changed = true;
      }
    }
    return changed;
  }

  /** All tracked processes, newest first, with liveness re-checked + persisted. */
  async list(): Promise<ProcessEntry[]> {
    const entries = await this.read();
    if (this.reconcileLiveness(entries)) await this.write(entries);
    return entries.sort((a, b) => b.startedAt - a.startedAt);
  }

  async runningCount(): Promise<number> {
    const entries = await this.list();
    return entries.filter((e) => e.status === "running").length;
  }

  async get(id: string): Promise<ProcessEntry | null> {
    return (await this.read()).find((e) => e.id === id) ?? null;
  }

  async add(entry: ProcessEntry): Promise<void> {
    const entries = await this.read();
    entries.push(entry);
    await this.write(entries);
  }

  async markExited(id: string, exitCode: number | null): Promise<void> {
    const entries = await this.read();
    const e = entries.find((x) => x.id === id);
    if (e && e.status === "running") {
      e.status = "exited";
      e.exitCode = exitCode;
      e.endedAt = Date.now();
      await this.write(entries);
    }
  }

  async markStopped(id: string): Promise<void> {
    const entries = await this.read();
    const e = entries.find((x) => x.id === id);
    if (e) {
      e.status = "stopped";
      e.endedAt = Date.now();
      await this.write(entries);
    }
  }
}
